/**
 * Validate a gate pass — QR SCAN (expo-camera, build #3) or the guest's PIN;
 * verdict card shows green/red with the guest + flat, then Allow entry
 * consumes the pass. The scanner is LAZY-loaded and capability-checked so it
 * never crashes web or an OTA'd 0.2.0 client (module absent there) — those
 * simply keep the PIN flow. Scanned payloads go to /api/gate/validate as a
 * generic `code` (handles BERO-<token>/<pin>/<id> and share codes).
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, Field, Input, Screen, TopBar } from '@/ui';

type Verdict = {
  verdict: 'valid' | 'expired' | 'not_yet' | 'used' | 'revoked' | 'not_found';
  message: string; guestName?: string; flatCode?: string; dailyWindow?: string | null;
  gatePass?: { id: number };
};

type CameraModule = typeof import('expo-camera');

export default function GuardValidate() {
  const { colors } = useTheme();
  const { t } = useT();
  const [pin, setPin] = React.useState('');
  const [result, setResult] = React.useState<Verdict | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [admitting, setAdmitting] = React.useState(false);
  const [admitted, setAdmitted] = React.useState(false);

  // QR scanner — module loaded lazily; the button appears only when it exists.
  const [cam, setCam] = React.useState<CameraModule | null>(null);
  const [scanning, setScanning] = React.useState(false);
  const [camDenied, setCamDenied] = React.useState(false);
  const scannedOnce = React.useRef(false);
  React.useEffect(() => {
    if (Platform.OS === 'web') return;
    import('expo-camera')
      .then((C) => { if (C?.CameraView) setCam(C); })
      .catch(() => {});
  }, []);

  const startScan = async () => {
    if (!cam) return;
    try {
      const perm = await cam.Camera.requestCameraPermissionsAsync();
      if (!perm.granted) { setCamDenied(true); return; }
      scannedOnce.current = false;
      setResult(null);
      setAdmitted(false);
      setScanning(true);
    } catch {
      setCamDenied(true);
    }
  };

  const runCheck = async (payload: { pin?: string; code?: string }) => {
    setBusy(true);
    setResult(null);
    setAdmitted(false);
    try {
      const body = await api.post<Verdict>('/api/gate/validate', payload);
      setResult(body);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setResult(status === 404
        ? { verdict: 'not_found', message: t('validate_not_found') }
        : { verdict: 'not_found', message: t('validate_check_error') });
    } finally {
      setBusy(false);
    }
  };

  const check = () => {
    if (pin.trim().length < 4 || busy) return;
    void runCheck({ pin: pin.trim() });
  };

  const onScanned = (data: string) => {
    if (scannedOnce.current) return; // CameraView fires repeatedly — take the first
    scannedOnce.current = true;
    setScanning(false);
    void runCheck({ code: data.trim() });
  };

  const admit = async () => {
    if (!result?.gatePass?.id || admitting) return;
    setAdmitting(true);
    try {
      await api.post<unknown>(`/api/gate/passes/${result.gatePass.id}/checkin`, {});
      setAdmitted(true);
    } catch { /* verdict card stays */ } finally {
      setAdmitting(false);
    }
  };

  const good = result?.verdict === 'valid';

  // Full-screen scanner takeover while scanning.
  if (scanning && cam) {
    const CameraView = cam.CameraView;
    return (
      <Screen scroll={false} safeTop>
        <View style={{ flex: 1 }}>
          <CameraView
            style={{ flex: 1 }}
            facing="back"
            barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
            onBarcodeScanned={(r) => onScanned(String(r?.data ?? ''))}
          />
          {/* aim frame + cancel, overlaid on the camera */}
          <View pointerEvents="box-none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}>
            <View style={{
              width: 230, height: 230, borderRadius: radius.xl,
              borderWidth: 3, borderColor: 'rgba(255,255,255,0.9)',
            }} />
            <AppText variant="bodyMd" weight="600" style={{ color: 'rgba(255,255,255,0.95)', marginTop: space.s4 }}>
              {t('validate_aim_qr')}
            </AppText>
            <Pressable
              accessibilityRole="button"
              onPress={() => setScanning(false)}
              style={{
                marginTop: space.s6, paddingHorizontal: space.s6, minHeight: 48, borderRadius: radius.full,
                alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.55)',
              }}
            >
              <AppText variant="bodyMd" weight="600" style={{ color: 'rgba(255,255,255,0.95)' }}>{t('cancel')}</AppText>
            </Pressable>
          </View>
        </View>
      </Screen>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title={t('validate_pass')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        {cam ? (
          <Button title={t('validate_scan_qr')} size="lg" onPress={() => void startScan()} />
        ) : null}
        {camDenied ? (
          <AppText variant="bodySm" tone="tertiary">
            {t('validate_cam_off')}
          </AppText>
        ) : null}
        <Field label={cam ? t('validate_pin_label_or') : t('validate_pin_label')} help={t('validate_pin_help')}>
          <Input
            value={pin}
            onChangeText={(v) => setPin(v.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="885751"
            keyboardType="numeric"
            style={{ fontSize: 24, letterSpacing: 8, textAlign: 'center', minHeight: 56 }}
          />
        </Field>
        <Button title={t('validate_check')} size="lg" loading={busy} disabled={pin.length < 4} onPress={check} kind={cam ? 'secondary' : 'primary'} />

        {result ? (
          <Card style={{
            borderWidth: 1.5,
            borderColor: good ? colors.success500 : colors.danger500,
            alignItems: 'center', gap: space.s2, paddingVertical: space.s5,
          }}>
            <Icon name={good ? 'check-circle' : 'x'} size={36} color={good ? colors.success500 : colors.danger500} />
            <AppText variant="headingSm" weight="600" style={{ color: good ? colors.success700 : colors.danger500 }}>
              {admitted ? t('validate_entered_logged') : result.message}
            </AppText>
            {result.guestName ? (
              <AppText variant="bodyMd" tone="secondary">
                {result.guestName} → {result.flatCode}
              </AppText>
            ) : null}
            {result.dailyWindow ? (
              <AppText variant="bodySm" tone="tertiary">{t('validate_daily_window', { window: result.dailyWindow })}</AppText>
            ) : null}
            {good && !admitted ? (
              <Button title={t('allow_entry')} size="lg" loading={admitting} onPress={() => void admit()} style={{ alignSelf: 'stretch', marginTop: space.s2 }} />
            ) : null}
          </Card>
        ) : null}
      </Screen>
    </Screen>
  );
}
